// components/CheckoutPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config/config';

import CategoriesNav from "../components/CategoriesNav";
import FlashBar from "../components/FlashBar";
import NavigationBar from "../components/NavigationBar";
import FooterSection from "../components/FooterSection";
import CartSection from "../components/CartSection";
import {
  CreditCard, Smartphone, Lock, Check, MapPin, User, ArrowLeft, Search,
  ChevronDown, ChevronUp, MessageCircle, Mail, Phone, Book, ShoppingBag,
  Truck, RotateCcw, HelpCircle, FileText, Users,
} from 'lucide-react';

// ─── Global refresh timestamps ───────────────────────────────────────────────
const LAST_REFRESH_KEY_LAYOUT   = 'last_layout_refresh_time';
const LAST_REFRESH_KEY_CHECKOUT = 'last_checkout_refresh_time';

const REFRESH_INTERVAL_LAYOUT   = 6 * 60 * 60 * 1000;   // 6 hours
const REFRESH_INTERVAL_CHECKOUT = 24 * 60 * 60 * 1000;  // 24 hours

const shouldRefresh = (key, interval) => {
  const last = localStorage.getItem(key);
  if (!last) return true;
  const lastTime = parseInt(last, 10);
  return Number.isNaN(lastTime) || (Date.now() - lastTime > interval);
};

const updateLastRefresh = (key) => {
  localStorage.setItem(key, Date.now().toString());
};

// ─── Cache Keys ──────────────────────────────────────────────────────────────
const CACHE_KEY_LAYOUT   = 'layout_settings__cache';
const CACHE_KEY_CHECKOUT = 'checkout_page_specific_cache';

const CACHE_TTL_LAYOUT   = REFRESH_INTERVAL_LAYOUT;
const CACHE_TTL_CHECKOUT = REFRESH_INTERVAL_CHECKOUT;

// ─────────────────────────────────────────────────────────────────────────────
const CheckoutPage = () => {
  const token = localStorage.getItem('token');

  // We use two loading states:
  // - isLayoutLoading → only for layout/config (should be very fast)
  // - cart is loaded separately and doesn't block UI
  const [isLayoutLoading, setIsLayoutLoading] = useState(true);

  // Layout pieces
  const [flashConfig,    setFlashConfig]    = useState(null);
  const [navConfig,      setNavConfig]      = useState(null);
  const [heroConfig,      setHeroConfig]      = useState(null);
  const [searchConfig,   setSearchConfig]   = useState(null);
  const [cartConfig,     setCartConfig]     = useState(null);
  const [footerConfig,   setFooterConfig]   = useState(null);
  const [catNavConfig,   setCatNavConfig]   = useState(null);
  const [CatConfig,   setCatConfig]   = useState(null);

  // Checkout page config
  const [checkoutConfig, setCheckoutConfig] = useState({});

  const [cartItems, setCartItems] = useState([]);

  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('mpesa');

  const defaultConfig = {
    activeDesign: 'side-by-side',
    title: 'Checkout',
    shippingTitle: 'Shipping Information',
    paymentTitle: 'Payment Method',
    reviewTitle: 'Review Your Order',
    emptyCartMessage: 'Your cart is empty',
    continueShoppingText: 'Continue Shopping',
    checkoutText: 'Complete Order',
    customStyles: {
      background: 'bg-gray-50',
      textColor: 'text-gray-900',
      buttonBg: 'bg-gray-900',
      buttonText: 'text-white',
      accentColor: 'text-green-600',
    },
  };

  const finalConfig = {
    ...defaultConfig,
    ...checkoutConfig,
  };

  const {
    activeDesign = defaultConfig.activeDesign,
    title = defaultConfig.title,
    shippingTitle = defaultConfig.shippingTitle,
    paymentTitle = defaultConfig.paymentTitle,
    reviewTitle = defaultConfig.reviewTitle,
    emptyCartMessage = defaultConfig.emptyCartMessage,
    continueShoppingText = defaultConfig.continueShoppingText,
    checkoutText = defaultConfig.checkoutText,
    customStyles = defaultConfig.customStyles,
  } = finalConfig;

  // ─── Cache Helpers ────────────────────────────────────────────────────────
  const getCached = (key, ttl) => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;
      const { data, timestamp } = JSON.parse(item);
      if (Date.now() - timestamp < ttl) return data;
      localStorage.removeItem(key);
      return null;
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  };

  const setCached = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now(),
      }));
    } catch {}
  };

  // ─── Load layout/config ───────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const initializeLayout = async () => {
      // 1. Load from cache immediately (fast path)
      const cachedLayout = getCached(CACHE_KEY_LAYOUT, CACHE_TTL_LAYOUT);
      if (cachedLayout) {
        setFlashConfig(cachedLayout.flash ?? null);
        setNavConfig(cachedLayout.nav ?? null);
        setSearchConfig(cachedLayout.search ?? null);
        setCartConfig(cachedLayout.cart ?? null);
        setHeroConfig(cachedLayout.hero ?? null);
        setFooterConfig(cachedLayout.footer ?? null);
        setCatNavConfig(cachedLayout.catNavConfig ?? null);
      }

      const cachedCheckout = getCached(CACHE_KEY_CHECKOUT, CACHE_TTL_CHECKOUT);
      if (cachedCheckout) {
        setCheckoutConfig(cachedCheckout);
      }

      // If we have cache, we can hide loading very early
      if ((cachedLayout || cachedCheckout) && mounted) {
        setIsLayoutLoading(false);
      }

      // 2. Check if refresh is needed
     // const needsLayoutRefresh   = shouldRefresh(LAST_REFRESH_KEY_LAYOUT, REFRESH_INTERVAL_LAYOUT);
      const needsLayoutRefresh   = true;
      const needsCheckoutRefresh = shouldRefresh(LAST_REFRESH_KEY_CHECKOUT, REFRESH_INTERVAL_CHECKOUT);

      // No refresh needed → we're done
      if (!needsLayoutRefresh && !needsCheckoutRefresh) {
        if (mounted) setIsLayoutLoading(false);
        return;
      }

      // 3. Background refresh (does NOT block UI)
      try {
        // Optional: only get CSRF when we know we will make authenticated calls
       // if (token) {
          await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, {
            withCredentials: true,
          }).catch(() => {});
       // }

        const promises = [];

        if (needsLayoutRefresh) {
          promises.push(
            Promise.allSettled([
              axios.get(`${config.API_URL}/api/layoutsettings`, { headers: { Authorization: `Bearer ${token}` } }),
              //axios.get(`${config.API_URL}/api/pullNavBarSettings`,   { headers: { Authorization: `Bearer ${token}` } }),
              //axios.get(`${config.API_URL}/api/pullSearchSettings`,   { headers: { Authorization: `Bearer ${token}` } }),
              //axios.get(`${config.API_URL}/api/pullCartSettings`,     { headers: { Authorization: `Bearer ${token}` } }),
              //axios.get(`${config.API_URL}/api/pullFooterSettings`,   { headers: { Authorization: `Bearer ${token}` } }),
              axios.get(`${config.API_URL}/api/customsettings/categoriesnav-2`, { headers: { Authorization: `Bearer ${token}` } }),
            ]).then(results => ({ type: 'layout', results }))
          );
        }

        // Checkout endpoint is commented out → skipped for now

        if (promises.length === 0) return;

        const settled = await Promise.allSettled(promises);

        let layoutData = null;

        settled.forEach(result => {
          if (result.status !== 'fulfilled') return;
          const val = result.value;

       /**   if (val.type === 'layout') {
            const [f, n, s, c, fo, cn] = val.results;
            layoutData = {
              flash: f.value?.data?.flashBarSettings ?? null,
              nav:   n.value?.data?.navBarSettings   ?? null,
              search: s.value?.data?.searchSettings ?? null,
              cart:  c.value?.data?.cartSettings    ?? null,
              footer: fo.value?.data?.footerSettings ?? null,
             catNavConfig: cn.value?.data?.customSettings ?? null,
            };
          }**/
          if (val.type === 'layout') {
            const [f,cn] = val.results;
            layoutData = {
              flash: f.value?.data?.flashBarSettings ?? null,
              nav:   f.value?.data?.navBarSettings   ?? null,
              search: f.value?.data?.searchSettings ?? null,
              cart:  f.value?.data?.cartSettings    ?? null,
              hero:  f.value?.data?.heroSettings    ?? null,
              cat:  f.value?.data?.catSettings    ?? null,
              footer: f.value?.data?.footerSettings ?? null,
             catNavConfig: cn.value?.data?.customSettings ?? null,
            };
          }
        });

        if (mounted && layoutData) {
          setFlashConfig(layoutData.flash);
          setNavConfig(layoutData.nav);
          setSearchConfig(layoutData.search);
          setCartConfig(layoutData.cart);
          setFooterConfig(layoutData.footer);
          setHeroConfig(layoutData.hero);
          setCatConfig(layoutData.cat);
          setCatNavConfig(layoutData.catNavConfig);

          setCached(CACHE_KEY_LAYOUT, layoutData);
          updateLastRefresh(LAST_REFRESH_KEY_LAYOUT);
        }
      } catch (err) {
        console.warn("Background layout refresh failed", err);
      } finally {
        if (mounted) setIsLayoutLoading(false);
      }
    };

    initializeLayout();

    return () => { mounted = false; };
  }, [token]);

  

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  // ─── Render ───────────────────────────────────────────────────────────────
 /** if (isLayoutLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }**/

  return (
    <>
    
      {flashConfig && <FlashBar config={flashConfig} />}
      {navConfig && <NavigationBar config={navConfig} cartConfig={cartConfig} CatConfig={CatConfig} />}
      {cartConfig && <CartSection config={cartConfig} isOpen={false} onClose={() => {}} />}
      

    </>
  );
};

export default CheckoutPage;